import Vue from 'vue';
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from 'axios';
import {settings} from '@/plugins/settings';
import {IErrors} from '@/model/IErrors';
import responseHandler from '@/plugins/errorHandling';
import {IChannel, IDriver, IRoute, IRouteOrder, IRouteStartingLocation, IRouteTimeSlot} from '@/model/Route/routeInterfaces';
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
} from '@/constants/pagination';

interface RouteState {
  routes: IRoute[];
  route: IRoute | null;
  routeOrders: IRouteOrder[];
  drivers: IDriver[];
  ordersForRouting: IRouteOrder[];
  startingLocations: IRouteStartingLocation[];
  channels: IChannel[];
  mapsApiKey: string;
  timeSlots: IRouteTimeSlot[];
  selectedTimeSlots: number[];
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
  errors: IErrors;
}

const state: RouteState = {
  routes: [],
  route: null,
  routeOrders: [],
  drivers: [],
  ordersForRouting: [],
  startingLocations: [],
  channels: [],
  mapsApiKey: '',
  timeSlots: [],
  selectedTimeSlots: [],
  currentPage: PAGINATION_FIRST_PAGE,
  lastPage: PAGINATION_DEFAULT_LAST_PAGE,
  perPage: PAGINATION_DEFAULT_PER_PAGE,
  total: PAGINATION_DEFAULT_TOTAL,
  errors: {},
};

const getters: GetterTree<RouteState, RootState> = {
  routes: (state) => state.routes,
  route: (state) => state.route,
  routeOrders: (state) => state.routeOrders,
  drivers: (state) => state.drivers,
  ordersForRouting: (state) => state.ordersForRouting,
  startingLocations: (state) => state.startingLocations,
  channels: (state) => state.channels,
  mapsApiKey: (state) => state.mapsApiKey,
  timeSlots: (state) => state.timeSlots,
  selectedTimeSlots: (state) => state.selectedTimeSlots,
  currentPage: (state) => state.currentPage,
  lastPage: (state) => state.lastPage,
  perPage: (state) => state.perPage,
  total: (state) => state.total,
  errors: (state) => state.errors,
};

const actions: ActionTree<RouteState, RootState> = {
  fetchRoutes: ({commit}, params: any = {}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes',
      method: 'GET',
      params,
    }),
    commit,
    'routesLoaded'
  ),
  fetchRoute: ({commit}, routeId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes/' + routeId,
      method: 'GET',
    }),
    commit,
    'routeLoaded'
  ),
  createRoute: ({commit}, data: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes',
      method: 'POST',
      data,
    }),
    commit,
    'routeCreated'
  ),
  updateRoute: ({commit}, data: {routeId: number; payload: any}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes/' + data.routeId,
      method: 'PATCH',
      data: data.payload,
    }),
    commit,
    'routeUpdated'
  ),
  markLegVisited: ({commit}, data: {routeId: number; legId: number; isVisited: boolean}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes/' + data.routeId + '/legs/' + data.legId,
      method: 'PATCH',
      data: {is_visited: data.isVisited},
    }),
    commit,
    'legVisited'
  ),
  deleteRoute: ({commit}, routeId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes/' + routeId,
      method: 'DELETE',
    }),
    commit,
    'routeDeleted'
  ),
  fetchDrivers: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes/drivers',
      method: 'GET',
    }),
    commit,
    'driversLoaded'
  ),
  fetchOrdersForRouting: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes/orders-for-routing',
      method: 'GET',
    }),
    commit,
    'ordersForRoutingLoaded'
  ),
  fetchStartingLocations: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'route-starting-locations',
      method: 'GET',
    }),
    commit,
    'startingLocationsLoaded'
  ),
  fetchChannels: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'channels',
      method: 'GET',
    }),
    commit,
    'channelsLoaded'
  ),
  createStartingLocation: ({commit}, data: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'route-starting-locations',
      method: 'POST',
      data,
    }),
    commit,
    'startingLocationCreated'
  ),
  deleteStartingLocation: ({commit}, id: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'route-starting-locations/' + id,
      method: 'DELETE',
    }),
    commit,
    'startingLocationDeleted'
  ),
  fetchTimeSlots: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'routes/time-slots',
      method: 'GET',
    }),
    commit,
    'timeSlotsLoaded'
  ),
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
  cleanError: ({commit}, index: any): any => {
    commit('cleanError', index);
  },
};

const mutations: MutationTree<RouteState> = {
  routesLoaded(state, payload: any) {
    if (!payload) {
      state.routes = [];
      return;
    }
    state.routes       = Array.isArray(payload.data) ? payload.data : [];
    state.currentPage  = payload.current_page || PAGINATION_FIRST_PAGE;
    state.lastPage     = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.perPage      = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
    state.total        = payload.total || PAGINATION_DEFAULT_TOTAL;
  },
  routeLoaded(state, payload: any) {
    state.route       = payload ? payload.route || null : null;
    state.routeOrders = payload ? payload.orders || [] : [];
  },
  routeCreated() {
    // navigation handled in component
  },
  routeUpdated() {
    // navigation handled in component
  },
  routeDeleted() {
    // navigation handled in component
  },
  legVisited(state, payload: any) {
    if (!state.route || !payload || !payload.id) {
      return;
    }
    const idx = state.route.legs.findIndex((leg) => leg.id === payload.id);
    if (idx !== -1) {
      Vue.set(state.route.legs, idx, {...state.route.legs[idx], is_visited: payload.is_visited, visited_at: payload.visited_at});
    }
  },
  driversLoaded(state, payload: any) {
    state.drivers = Array.isArray(payload) ? payload : [];
  },
  startingLocationsLoaded(state, payload: any) {
    state.startingLocations = payload && Array.isArray(payload.data) ? payload.data : [];
    state.mapsApiKey        = payload && payload.google_maps_api_key ? payload.google_maps_api_key : '';
  },
  startingLocationCreated(state, payload: any) {
    if (payload && payload.id) {
      state.startingLocations = state.startingLocations.concat([payload]);
    }
  },
  startingLocationDeleted(state) {
    // list refresh handled by component after deletion
  },
  ordersForRoutingLoaded(state, payload: any) {
    state.ordersForRouting = Array.isArray(payload) ? payload : [];
  },
  channelsLoaded(state, payload: any) {
    state.channels = Array.isArray(payload) ? payload : [];
  },
  timeSlotsLoaded(state, payload: any) {
    state.timeSlots         = payload && payload.timeslots ? payload.timeslots : [];
    state.selectedTimeSlots = payload && payload.selected_timeslots ? payload.selected_timeslots : [];
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },
};

const namespaced: boolean = true;

export const routeModule: Module<RouteState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
